import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../store/useCartStore';
import { useAuthStore } from '../store/useAuthStore';
import { useToast } from '../context/ToastContext';
import FallbackImage from '../components/FallbackImage';
import { createOrder, fetchProductById, fetchProductVariants, type Variant } from '../utils/apiHelpers';
import type { CartItem } from '../store/useCartStore';

const COUNTRIES = [{ value: 'Pakistan', label: 'Pakistan' }];
const STATES = [{ value: 'Punjab', label: 'Punjab' }, { value: 'Sindh', label: 'Sindh' }, { value: 'KPK', label: 'KPK' }, { value: 'Balochistan', label: 'Balochistan' }];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, updateQuantityForLine, removeLineItem, clearCart } = useCartStore();
  const { user } = useAuthStore();
  const { showToast } = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [cashOnDelivery, setCashOnDelivery] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('Pakistan');
  const [street, setStreet] = useState('');
  const [apartment, setApartment] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('Punjab');
  const [postcode, setPostcode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  useEffect(() => {
    if (user) {
      const name = (user as { first_name?: string; last_name?: string }).first_name
        ? `${(user as { first_name?: string }).first_name || ''} ${(user as { last_name?: string }).last_name || ''}`.trim()
        : user.username || '';
      if (name) setFirstName(name.split(' ')[0] || '');
      if (name) setLastName(name.split(' ').slice(1).join(' ') || '');
      if (user.email) setEmail(user.email);
      const u = user as { phone?: string; address?: string };
      if (u.phone) setPhone(u.phone);
      if (u.address) setStreet(u.address);
    }
  }, [user]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const deliveryCharges = subtotal >= 2000 ? 0 : 200;
  const total = subtotal + deliveryCharges;

  const guestName = [firstName, lastName].filter(Boolean).join(' ') || (user?.username ?? '');
  const guestAddress = [street, apartment, city, state, postcode, country].filter(Boolean).join(', ');

  const validate = (): boolean => {
    if (!firstName.trim()) {
      showToast('First name is required', 'error');
      return false;
    }
    if (!lastName.trim()) {
      showToast('Last name is required', 'error');
      return false;
    }
    if (!country.trim()) {
      showToast('Country / Region is required', 'error');
      return false;
    }
    if (!street.trim()) {
      showToast('Street address is required', 'error');
      return false;
    }
    if (!city.trim()) {
      showToast('Town / City is required', 'error');
      return false;
    }
    if (!state.trim()) {
      showToast('State / County is required', 'error');
      return false;
    }
    if (!postcode.trim()) {
      showToast('Postcode / ZIP is required', 'error');
      return false;
    }
    if (!phone.trim()) {
      showToast('Phone is required', 'error');
      return false;
    }
    if (!email.trim()) {
      showToast('Email address is required', 'error');
      return false;
    }
    return true;
  };

  const buildSpecificationFromVariant = (variant: Variant): Record<string, unknown> => {
    const spec: Record<string, unknown> = {};
    if (variant.color) spec.color = variant.color;
    if (variant.size) spec.size = variant.size;
    if (variant.customAttributes && Object.keys(variant.customAttributes).length > 0) {
      Object.assign(spec, variant.customAttributes);
    }
    if (variant.sku && !spec.sku) spec.sku = variant.sku;
    return spec;
  };

  const handlePlaceOrder = async () => {
    if (items.length === 0) {
      showToast('Your cart is empty', 'error');
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    try {
      const parseProductId = (id: number | string): number => {
        const n = Number(id);
        if (Number.isFinite(n) && !Number.isNaN(n)) return n;
        const digits = String(id).match(/\d+/);
        if (digits) return Number(digits[0]);
        throw new Error(`Invalid product id: ${id}`);
      };

      const orderItems = items.map((item) => {
        const productId = parseProductId(item.id);
        const row: { product: number; quantity: number; price: number; variant_id?: number; specification: Record<string, unknown> } = {
          product: productId,
          quantity: item.quantity,
          price: item.price,
          specification: item.specification || {},
        };
        if (item.variant_id != null) {
          row.variant_id = Number(item.variant_id);
        }
        return row;
      });

      // Fetch variants for items without variant_id and auto-fill specification
      const itemsNeedingVariants = orderItems.filter((oi) => !oi.variant_id && Object.keys(oi.specification).length === 0);
      
      for (const item of itemsNeedingVariants) {
        try {
          const variants = await fetchProductVariants(item.product);
          console.log(`🔍 Fetched ${variants.length} variants for product ${item.product}`);
          
          if (variants.length > 0) {
            const firstVariant = variants[0];
            item.variant_id = Number(firstVariant.id);

            const spec = buildSpecificationFromVariant(firstVariant);
            if (Object.keys(spec).length > 0) {
              item.specification = spec;
              console.log(`✅ Auto-filled specification for product ${item.product}:`, spec);
            } else {
              try {
                const product = await fetchProductById(item.product);
                const productSpec =
                  product.specification ??
                  (product.extra?.specification as Record<string, unknown> | undefined);
                if (productSpec && Object.keys(productSpec).length > 0) {
                  item.specification = productSpec;
                  console.log(`✅ Auto-filled specification from product details for product ${item.product}:`, productSpec);
                } else {
                  item.specification = { variant: `variant-${firstVariant.id}` };
                  console.log(`ℹ️  No variant/product specification found for product ${item.product}, using fallback specification`, item.specification);
                }
              } catch (productError) {
                item.specification = { variant: `variant-${firstVariant.id}` };
                console.warn(`⚠️ Could not fetch product details for product ${item.product}, using fallback specification:`, productError);
              }
            }
          } else {
            try {
              const product = await fetchProductById(item.product);
              const productSpec =
                product.specification ??
                (product.extra?.specification as Record<string, unknown> | undefined);
              if (productSpec && Object.keys(productSpec).length > 0) {
                item.specification = productSpec;
                console.log(`✅ Auto-filled specification from product details for product ${item.product}:`, productSpec);
              } else {
                item.specification = { variant: 'default' };
                console.log(`ℹ️  No variants or product specification found for product ${item.product}, using default specification`);
              }
            } catch (productError) {
              item.specification = { variant: 'default' };
              console.warn(`⚠️ Could not fetch product details for product ${item.product}, using default specification:`, productError);
            }
          }
        } catch (e) {
          // Variant fetch failed, use default specification
          item.specification = { variant: 'default' };
          console.warn(`⚠️  Could not fetch variants for product ${item.product}, using default:`, e);
        }
      }

      const payload = {
        guest_name: guestName.trim() || 'Guest',
        guest_email: email.trim(),
        guest_phone: phone.trim(),
        guest_address: guestAddress.trim(),
        subtotal: Number(subtotal),
        delivery_charges: Number(deliveryCharges),
        payment_status: cashOnDelivery ? 'pending' : 'unpaid',
        status: 'pending',
        order_items: orderItems,
        order_notes: orderNotes.trim(),
        use_frontend_prices: true,
      };
      console.log('📦 Sending order payload:', JSON.stringify(payload, null, 2));
      await createOrder(payload);
      clearCart();
      showToast('Order placed successfully!', 'success');
      navigate('/');
    } catch (err: unknown) {
      console.error('Order placement error:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: unknown; status?: number; statusText?: string } };
        console.error('❌ Server response:', axiosError.response?.status, axiosError.response?.statusText);
        console.error('📋 Response data:', JSON.stringify(axiosError.response?.data, null, 2));
      }
      let message = 'Failed to place order. Please try again.';
      const res = err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: unknown; status?: number } }).response : undefined;
      const data = res?.data;
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (Array.isArray(data)) {
          message = data.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ');
        } else if (typeof data === 'object' && data !== null) {
          message = Object.entries(data as Record<string, unknown>)
            .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
            .join('; ');
        }
      } else if (res?.status) {
        message = `Server returned status ${res.status}`;
      }
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0 && !submitting) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-[var(--foreground)] mb-4">Checkout</h1>
        <p className="text-gray-500 mb-4">Your cart is empty.</p>
        <Link
          to="/shop"
          className="inline-block py-2 px-4 bg-[#ff9100] text-white rounded-lg hover:bg-[#ff7a00]"
        >
          Continue shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-[var(--foreground)] mb-6">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left - Billing & Payment */}
        <div className="space-y-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[var(--foreground)] mb-4">
              Billing Details
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company name (optional)
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country / Region <span className="text-red-500">*</span>
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="House number and street name"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apartment, unit, etc. (optional)
              </label>
              <input
                type="text"
                value={apartment}
                onChange={(e) => setApartment(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Town / City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State / County <span className="text-red-500">*</span>
              </label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
              >
                {STATES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode / ZIP <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order notes (optional)
              </label>
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Notes about your order, e.g. special notes for delivery."
                rows={3}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[var(--foreground)] mb-4">
              Payment Information
            </h2>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cashOnDelivery}
                onChange={(e) => setCashOnDelivery(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-[#ff9100]"
              />
              <div>
                <span className="font-medium">Cash on delivery</span>
                <p className="text-sm text-gray-500 mt-0.5">Pay with cash upon delivery.</p>
              </div>
            </label>
            <p className="text-xs text-gray-500 mt-4">
              Your personal data will be used to process your order, support your experience
              throughout this website, and for other purposes described in our{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">privacy policy</Link>.
            </p>
          </div>
        </div>

        {/* Right - Your Order */}
        <div className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[var(--foreground)] mb-4">
              Your Order
            </h2>
            <ul className="space-y-4 border-b border-gray-200 pb-4">
              {items.map((item) => (
                <OrderLineItem
                  key={`${item.id}-${JSON.stringify(item.specification)}`}
                  item={item}
                  onUpdateQty={(q) => updateQuantityForLine(item, q)}
                  onRemove={() => removeLineItem(item)}
                />
              ))}
            </ul>
            <div className="space-y-2 py-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>Rs {subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between font-semibold text-base pt-2">
                <span>Total</span>
                <span className="text-[#ff9100]">Rs {total.toLocaleString()}</span>
              </div>
            </div>
            {deliveryCharges === 0 && (
              <p className="text-sm text-[#ff9100] font-medium mb-4">
                Your order qualifies for free shipping!
              </p>
            )}
            <button
              type="button"
              onClick={handlePlaceOrder}
              disabled={submitting}
              className="w-full py-3 bg-[#ff9100] text-white rounded-lg font-semibold hover:bg-[#ff7a00] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? 'Placing order...' : 'Place Order'}
            </button>
            <Link
              to="/cart"
              className="block text-center text-sm text-gray-500 hover:underline mt-3"
            >
              Back to cart
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

function OrderLineItem({
  item,
  onUpdateQty,
  onRemove,
}: {
  item: CartItem;
  onUpdateQty: (q: number) => void;
  onRemove: () => void;
}) {
  const lineTotal = item.price * item.quantity;
  return (
    <li className="flex gap-4">
      <div className="w-16 h-16 rounded border border-gray-200 overflow-hidden flex-shrink-0 bg-gray-50">
        <FallbackImage src={item.image} alt={item.name} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[var(--foreground)] text-sm line-clamp-2">
          {item.name}
        </p>
        <p className="text-[#ff9100] font-semibold mt-0.5">
          Rs {item.price.toLocaleString()}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={() => onUpdateQty(Math.max(1, item.quantity - 1))}
            className="w-7 h-7 rounded border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
          >
            -
          </button>
          <span className="w-8 text-center text-sm">{item.quantity}</span>
          <button
            type="button"
            onClick={() => onUpdateQty(item.quantity + 1)}
            className="w-7 h-7 rounded border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
          >
            +
          </button>
        </div>
      </div>
      <div className="flex flex-col items-end justify-between">
        <button
          type="button"
          onClick={onRemove}
          className="text-gray-400 hover:text-red-600 p-1"
          aria-label="Remove"
        >
          ×
        </button>
        <span className="text-sm font-medium">Rs {lineTotal.toLocaleString()}</span>
      </div>
    </li>
  );
}
